import {
  Badge, Card, EmptyState, ErrorState, GalleryMedia, LoadingState, Row, Select, Stack,
  shiftIsoDate, useApi, useI18n,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The gallery (§4.6).
 *
 * A parent sees photographs their own child is tagged in and no others — the requirement the original
 * client was most emphatic about. That is decided on the server; this screen only renders what comes back
 * and never filters on its own, because a filter here would be a filter anyone could remove.
 *
 * Grouped by date with counts, which is how the client described the screen they wanted.
 */
export function Photos({ childId }: { childId: number }) {
  const api = useApi();
  const { t, tn, formatDate, todayLabel } = usePhotoStrings();
  const [occasionId, setOccasionId] = useState<number | ''>('');

  const occasions = useQuery({ queryKey: ['occasions'], queryFn: () => api.photos.occasions() });

  const gallery = useQuery({
    queryKey: ['photos', childId, occasionId],
    queryFn: () => api.photos.gallery({
      childId,
      ...(occasionId === '' ? {} : { occasionId }),
    }),
  });

  if (gallery.isPending) return <LoadingState />;
  if (gallery.isError) {
    return <ErrorState error={gallery.error} onRetry={() => void gallery.refetch()} />;
  }

  if (gallery.data.groups.length === 0) {
    return <EmptyState icon="📷" title={t('photos.empty')} />;
  }

  return (
    <Stack>
      {/* FR-PHO-02 asks for filtering by occasion. Activities are the occasions in practice. */}
      <Select
        aria-label={t('photos.occasion')}
        value={occasionId}
        onChange={(event) => setOccasionId(event.target.value === '' ? '' : Number(event.target.value))}
      >
        <option value="">{t('photos.allOccasions')}</option>
        {(occasions.data ?? []).map((occasion) => (
          <option key={occasion.id} value={occasion.id}>{occasion.name}</option>
        ))}
      </Select>

      {gallery.data.groups.map((group) => (
        <Card
          key={group.date}
          title={
            <Row>
              <span>{todayLabel(group.date) ?? formatDate(group.date)}</span>
              <Badge tone="count">{tn('photos.count', group.count)}</Badge>
            </Row>
          }
        >
          <div className="dg-photo-grid">
            {group.photos.map((photo) => (
              <figure key={photo.id} className="dg-photo">
                <GalleryMedia item={photo} />
                {photo.caption && <figcaption>{photo.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </Card>
      ))}
    </Stack>
  );
}

/** Wraps the shared hook with the one piece of wording this screen needs beyond it. */
function usePhotoStrings() {
  const i18n = useI18n();

  return {
    ...i18n,
    /** "Today" and "yesterday" read better than a date on the two groups a parent opens most. */
    todayLabel: (date: string): string | null => {
      const today = i18n.today();
      if (date === today) return i18n.t('day.today');

      return date === shiftIsoDate(today, -1) ? i18n.t('day.yesterday') : null;
    },
  };
}
